import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { CustomLightColorName, OnOff, RoomName } from 'src/graphql/generated/schema';
import { ColorPreset, LightDevice } from '../../interfaces/lights.interface';
import { combineLatest, map, Observable, of, shareReplay, startWith, switchMap, take, takeUntil, tap, distinctUntilChanged } from 'rxjs';
import { ModalController, ToastController } from '@ionic/angular';
import { preparePresetColor } from '../../helpers/color-convert-helper';
import { CommonPage } from '../../common-page/common-page';
import { MqttConnectorService } from 'src/mqtt/services/mqtt-connector.service';
import { IShadowState } from 'src/mqtt/interfaces/interfaces';
import { actionId, actionType, roomsNames } from 'src/mqtt/actions-list';
import { LightsControlService } from '../../services/lights-control.service';
import { getActionKey } from 'src/mqtt/utils/device-name-parser';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { BuildingStateService } from 'src/app/services/building-state.service';
import { getZoneDisplayNames } from 'src/config/building-lighting-config';
import { brightness255ToPercent } from '../../helpers/brightness-helper';

interface ILightDevicesMap {
  [key: string]: LightDevice;
}

@Component({
  selector: 'light-control',
  templateUrl: './light-control.component.html',
  styleUrls: ['./light-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LightControlComponent extends CommonPage implements OnInit {
  public static WHITE_LIGHT: string = '#FFFFFF';

  public lightDeviceControls: ILightDevicesMap = {}

  // Store presets
  public colorPresets: ColorPreset[] = [];
  // Which preset the resident explicitly tapped, per light. Lives here (not in the child)
  // so it survives the settings panel being destroyed/recreated on close/reopen.
  public selectedPresetByDevice = new Map<string, number>();
  public allEnabled: boolean = true;

  public controlledDevice?: LightDevice;

  /** Reachability for control; gates taps so they don't silently no-op. */
  public controlAvailability: 'ready' | 'reconnecting' | 'needs_reauth' = 'reconnecting';

  // Optimistic update tracking
  private isOptimisticUpdate = false;
  private optimisticUpdateTimeout: any;
  private pendingUpdates = new Map<string, any>();
  private updateDebounceTimer: any;

  public get lightDevices(): LightDevice[] {
    return Object.values(this.lightDeviceControls);
  }

  constructor(
    private ref: ChangeDetectorRef,
    public toastController: ToastController,
    public modalCtrl: ModalController,
    private mqtt: MqttConnectorService,
    private lightsControlService: LightsControlService,
    private analyticsService: AnalyticsService,
    private buildingStateService: BuildingStateService,
  ) {
    super(toastController, modalCtrl);
  }

  public ngOnInit(): void {
    this.mqtt.getControlAvailability().pipe(
      takeUntil(this.destroy$),
    ).subscribe((availability) => {
      this.controlAvailability = availability;
      this.ref.markForCheck();
    });

    let firstDeviceLogged = false;
    this.mqtt.getMessageListener(`${actionType.light}.${actionId.allLights}`).pipe(
      takeUntil(this.destroy$)
    ).subscribe((message: IShadowState | undefined) => {
      if (message?.reported?.entity_id && message?.reported?.attributes) {
        this.allEnabled = <OnOff>message?.reported?.state?.toUpperCase() === OnOff.On;
      }
      this.ref.markForCheck();
    });

    this.lightsControlService.lightsUnits.pipe(
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      switchMap((entities: string[]) => {
        
        // If no entities, return empty result with proper type
        if (!entities.length) return of({ messages: [] as [(IShadowState | undefined), (IShadowState | undefined), (IShadowState | undefined)][], entities: [] as string[] });
        
        // Map each entity to its message listeners with default values
        return combineLatest(entities.map((entity: string) =>
          combineLatest([
            this.mqtt.getMessageListener(`${actionType.light}.${entity}${actionId.lights}`).pipe(
              // Use a default undefined value to prevent blocking
              startWith(undefined)
            ),
            this.mqtt.getMessageListener(`${actionType.inputText}.${entity}${actionId.lights}`).pipe(
              // Use a default undefined value to prevent blocking
              startWith(undefined)
            ),
            this.mqtt.getMessageListener(`${actionType.inputNumber}.${entity}${actionId.lightBrightness}`).pipe(
              // Use a default undefined value to prevent blocking
              startWith(undefined)
            )
          ])
        )).pipe(
          // Include entities in the result
          map(messages => ({ messages, entities })),
          // Prevent duplicate emissions
          distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(({ messages, entities }: { messages: [(IShadowState | undefined), (IShadowState | undefined), (IShadowState | undefined)][], entities: string[] }) => {
      // First, remove any placeholder devices if we have real devices
      const hasRealDevices = messages.some(([message]) => message?.reported?.entity_id);
      if (hasRealDevices) {
        // Remove all placeholder devices
        Object.keys(this.lightDeviceControls)
          .filter(key => key.startsWith('pending_'))
          .forEach(key => delete this.lightDeviceControls[key]);
      }
      
      // Track which device IDs we've processed
      const processedDeviceIds = new Set<string>();
      
      // Track which device IDs we've processed
      
      messages?.forEach(([message, name, brightness]: [(IShadowState | undefined), (IShadowState | undefined), (IShadowState | undefined)]) => {
        if (message?.reported?.entity_id) {
          const deviceId = getActionKey(message?.reported?.entity_id, actionId.lights);
          
          // If we're in optimistic update mode, store updates for later
          if (this.isOptimisticUpdate && deviceId !== 'all') {
            this.pendingUpdates.set(deviceId, { message, name, brightness });
            return;
          }
          
          const device = this.lightsControlService.getDeviceBaseData(deviceId, name?.reported?.state ?? '', message);

          // Display the light's real brightness, not the saved-brightness helper,
          // so switch- and scene-driven changes appear without a reopen.
          if (device) {
            device.brightness = brightness255ToPercent(
              message?.reported?.attributes?.['brightness'],
            );
          }

          const existsDevice = this.lightDeviceControls[deviceId]
          this.lightDeviceControls[deviceId] = {
            ...(existsDevice ?? {}),
            ...device
          }
          processedDeviceIds.add(deviceId);
          
        }
      });
      
      // Add placeholder devices for any entities we haven't processed yet
      entities.forEach((entityId: string, index: number) => {
        if (!processedDeviceIds.has(entityId) && !this.lightDeviceControls[entityId]) {
          // Create a disabled placeholder device with appropriate names
          let title = 'Light';

          // Get building-aware zone names
          const building = this.buildingStateService.getCurrentBuilding();
          const zoneNames = getZoneDisplayNames(building?.id);

          // Map entity IDs to friendly names using building config
          if (zoneNames[entityId]) {
            title = zoneNames[entityId];
          } else if (entityId === 'livingspace_a') {
            title = 'Living Room';
          } else if (entityId === 'bedroom_a') {
            title = 'Bedroom';
          } else if (entityId === 'hallway_a') {
            title = 'Spa Suite';
          } else if (entityId.startsWith('pending_')) {
            const titles = ['Living Room', 'Spa Suite', 'Bedroom'];
            title = titles[index] || `Zone ${index + 1}`;
          }

          this.lightDeviceControls[entityId] = {
            deviceId: entityId,
            title: title,
            active: false,
            isUnavailable: true,
            brightColor: '#FFFFFF',
            temperature: 0,
            brightness: 0
          };
        }
      });
      
      // Debounce UI updates to batch them
      this.debounceUIUpdate();
    });

    combineLatest([
      this.mqtt.getMessageListener(`${actionType.inputText}.${actionId.customLightsColor1}`),
      this.mqtt.getMessageListener(`${actionType.inputText}.${actionId.customLightsColor2}`),
      this.mqtt.getMessageListener(`${actionType.inputText}.${actionId.customLightsColor3}`),
    ]).pipe(takeUntil(this.destroy$)).subscribe((messages: (IShadowState | undefined)[]) => {
      const colors = messages?.map((message: IShadowState | undefined) => {
        return message?.reported?.state && message?.reported?.state !== 'unavailable' ? message?.reported?.state : undefined;
      });

      if (!this.colorPresets?.length) {
        this.colorPresets = this.initColorPresets(colors);
      } else {
        this.colorPresets = this.updateColorPresets(colors);
      }
    });
  }

  /** Gate a tap when the home is unreachable; tell the resident why. */
  private ensureControllable(): boolean {
    if (this.controlAvailability === 'ready') return true;
    this.presentToast(
      this.controlAvailability === 'needs_reauth'
        ? 'Please sign in again to control your home.'
        : 'Reconnecting to your home. Try again in a moment.',
    );
    return false;
  }

  public switchCurrentLightDevice(deviceId: string) {
    const device = this.lightDeviceControls[deviceId];
    if (!device) return;
    if (!this.ensureControllable()) return;

    const newState = !device.active;
    this.analyticsService.trackWidgetInteraction('lights', 'single_light_toggle', { deviceId, state: newState ? 'on' : 'off' });

    this.lightsControlService.switchLight(deviceId, newState).subscribe({
      next: () => {
        this.allEnabled = this.lightDevices.map((device) => device.active).reduce((prev, curr) => prev || curr, false);
        this.ref.markForCheck();
      },
      error: () => {
        this.presentToast("Couldn't reach your home. The light didn't change.");
        this.ref.markForCheck();
      },
    });
  }

  public switchAllDevices() {
    if (!this.ensureControllable()) return;

    // Snapshot for rollback if the publish fails.
    const prevAllEnabled = this.allEnabled;
    const prevActive = new Map(
      Object.keys(this.lightDeviceControls).map((id) => [id, this.lightDeviceControls[id].active]),
    );

    this.allEnabled = !this.allEnabled;

    this.analyticsService.trackWidgetInteraction('lights', 'all_lights_toggle', { state: this.allEnabled ? 'on' : 'off' });

    // Set optimistic update flag to prevent MQTT updates from causing flicker
    this.isOptimisticUpdate = true;

    // Clear any existing timeout
    if (this.optimisticUpdateTimeout) {
      clearTimeout(this.optimisticUpdateTimeout);
    }

    // Update all devices immediately (optimistic update)
    Object.keys(this.lightDeviceControls).forEach(deviceId => {
      this.lightDeviceControls[deviceId].active = this.allEnabled;
    });

    // Mark for change detection immediately
    this.ref.markForCheck();

    // Send MQTT command to control all lights
    const entityId = `${actionType.light}.${actionId.allLights}`;
    this.mqtt.setStateValue(entityId, this.allEnabled ? 'on' : 'off').pipe(
      take(1)
    ).subscribe({
      next: () => {
        console.log(`All lights turned ${this.allEnabled ? 'on' : 'off'}`);

        // Keep optimistic flag on for 2 seconds to ignore individual light updates
        this.optimisticUpdateTimeout = setTimeout(() => {
          this.isOptimisticUpdate = false;
          // Apply any pending updates that arrived during optimistic period
          this.applyPendingUpdates();
        }, 2000);
      },
      error: () => {
        // Publish failed: roll back the optimistic state instead of leaving the
        // lights looking toggled when nothing reached the home.
        this.isOptimisticUpdate = false;
        this.allEnabled = prevAllEnabled;
        prevActive.forEach((active, id) => {
          if (this.lightDeviceControls[id]) this.lightDeviceControls[id].active = active;
        });
        this.presentToast("Couldn't reach your home. The lights didn't change.");
        this.ref.markForCheck();
      },
    });
  }

  public showSettingsPanel(device: LightDevice) {
    // VI-558 (round 3): the off-state colour-picker icon is now clickable so residents can
    // reach settings on an off-but-available light, but that click also reaches unavailable
    // devices and the pending_* placeholders created above while entities haven't reported
    // yet — either would let a drag write a helper for a light that can never answer. Guard
    // on isUnavailable (set both for a real HA "unavailable" state and for placeholders)
    // rather than `active`, so a genuinely off-but-reachable light stays reachable.
    if (device.isUnavailable) return;
    this.controlledDevice = device;
  }

  public hideSettingsPanel() {
    this.controlledDevice = undefined;
    this.colorPresets.forEach((preset) => {
      preset.selected = false;
    });
  }

  private initColorPresets(colors: (string | undefined)[]): ColorPreset[] {
    return [
      {
        id: 0,
        value: preparePresetColor(colors[0] || undefined),
        entityName: CustomLightColorName.CustomLightColor_01,
        selected: false,
        canChange: true,
      },
      {
        id: 1,
        value: preparePresetColor(colors[1] || undefined),
        entityName: CustomLightColorName.CustomLightColor_02,
        selected: false,
        canChange: true,
      },{
        id: 2,
        value: preparePresetColor(colors[2] || undefined),
        entityName: CustomLightColorName.CustomLightColor_03,
        selected: false,
        canChange: true,
      },
      {
        id: 3,
        value: LightControlComponent.WHITE_LIGHT,
        entityName: CustomLightColorName.CustomLightColor_04,
        selected: false,
        canChange: false,
      },
    ];
  }

  private updateColorPresets(colors: (string | undefined)[]): ColorPreset[] {
    return this.colorPresets.map((preset: ColorPreset, index: number) => {
      if (index === 3) return preset;
      return {
        ...preset,
        value: preparePresetColor(colors[index] || undefined),
      }
    });
  }
  
  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    
    // Clear any pending timers
    if (this.optimisticUpdateTimeout) {
      clearTimeout(this.optimisticUpdateTimeout);
    }
    if (this.updateDebounceTimer) {
      clearTimeout(this.updateDebounceTimer);
    }
  }
  
  private debounceUIUpdate(): void {
    // Clear existing timer
    if (this.updateDebounceTimer) {
      clearTimeout(this.updateDebounceTimer);
    }
    
    // Set new timer to batch updates
    this.updateDebounceTimer = setTimeout(() => {
      this.ref.markForCheck();
    }, 50); // 50ms debounce
  }
  
  private applyPendingUpdates(): void {
    // Apply any updates that were queued during optimistic update period
    this.pendingUpdates.forEach(({ message, name }, deviceId) => {
      const device = this.lightsControlService.getDeviceBaseData(deviceId, name?.reported?.state ?? '', message);

      // Same truth source as the main handler above: the light entity's own reported
      // brightness attribute, not the saved-brightness helper. undefined is a legal
      // (and correct) value here — it means the light replayed as off.
      if (device) {
        device.brightness = brightness255ToPercent(
          message?.reported?.attributes?.['brightness'],
        );
      }

      const existsDevice = this.lightDeviceControls[deviceId];
      this.lightDeviceControls[deviceId] = {
        ...(existsDevice ?? {}),
        ...device
      };
    });
    
    // Clear pending updates
    this.pendingUpdates.clear();
    
    // Update UI
    this.ref.markForCheck();
  }
}
